import { useEffect, useState } from 'react';

// HOW A PRESS-OPENED OVERLAY GETS ITS CHUNK (T-323 / BUG-157).
//
// Three surfaces are code-split behind a press, each with `fallback={null}` because the only thing on screen
// before they open is the page they sit over: the search palette (`search/lazy.tsx`, T-049), the review
// dialog (`routes/review/lazy.tsx`) and the date panel (`app/period/lazyPopover.tsx`, both T-321). They were
// `React.lazy` + `Suspense`, and that shape **livelocks React inside `act()`**:
//
//   a press is a blocking-priority update, so `fireEvent.click` → `act()` flushes it synchronously
//   (`flushActQueue`'s own `do { callback = callback(true) } while (callback !== null)` loop) → the boundary
//   mounts for the first time, `React.lazy` throws its still-pending thenable → `performWorkOnRoot` cannot
//   finish, hands back a continuation, and the loop renders the root again. The thenable can only settle on a
//   microtask, and a synchronous loop never yields one. 100 % of one core, a fresh work-in-progress tree per
//   iteration, no test failing and no timeout firing (timers never run either) — about 3 MB of heap a second
//   until the worker hits Node's 4 GB ceiling and `tinypool` loses the channel. That is BUG-157: the gate's
//   `test` step died at 4,054 of 4,131 MB with 0 failing tests, 24.6 minutes into one file.
//
// Nothing about it is specific to tests — `act()` is just the one place React is asked to finish a blocking
// render without ever yielding, which is exactly what a test harness does. The fix is to stop suspending:
// the module is loaded imperatively and held outside React, and the boundary renders the panel on the first
// commit where the module is in hand. That is what `Suspense fallback={null}` already looked like from the
// outside — nothing, then the panel — so no screen, no id and no chunk boundary changes, and the idle
// prefetch each boundary already runs means the module is normally in hand before the press.
//
// A failed chunk is still a render-phase throw, so it reaches the route error boundary exactly as
// `React.lazy`'s rejection did (BUG-054's `vite:preloadError` path is a window event and is untouched).

export interface OverlayModule<M> {
  /**
   * The module if it has already arrived, else `null`. Safe to read while rendering: it only ever goes from
   * `null` to the module, never back, which is the same monotonic promise `React.lazy`'s own cache makes.
   * It never throws — see `failure`.
   */
  peek: () => M | null;
  /** Why the chunk will not load, or `null`. Read separately so only a reader who asked for it is shown. */
  failure: () => unknown;
  /** Start the load, or join the one already in flight. Resolves to the module. */
  load: () => Promise<M>;
}

/**
 * Wrap a dynamic `import()` so the module can be asked for, re-asked for and read synchronously once it has
 * arrived. The `import()` stays in the caller's own file, which is what keeps it a chunk boundary (and what
 * `app/shell/bundle.test.ts` reads).
 */
export function overlayModule<M>(importer: () => Promise<M>): OverlayModule<M> {
  let loaded: M | null = null;
  let failed: unknown = null;
  let inFlight: Promise<M> | null = null;
  return {
    peek: () => loaded,
    failure: () => failed,
    load: () => {
      inFlight ??= importer().then(
        (module) => (loaded = module),
        (error: unknown) => {
          failed = error ?? new Error('chunk failed to load');
          throw failed;
        },
      );
      return inFlight;
    },
  };
}

/**
 * The module for an overlay that is `wanted` (open), or `null` while it is still coming. Asks for it from an
 * effect — never from the render — and re-renders the caller once it is there.
 *
 * A chunk that will not load is re-thrown **while rendering, and only while the overlay is wanted**, so it
 * reaches the nearest error boundary with the real reason exactly as `React.lazy`'s rejection did. The
 * `wanted` guard is the half that matters: these boundaries are mounted (closed) by the shell on every
 * framed route, and the idle prefetch can fail long before anyone presses anything — throwing then would
 * take the whole screen down over a panel nobody asked for.
 */
export function useOverlayModule<M>(module: OverlayModule<M>, wanted: boolean): M | null {
  const [, arrived] = useState(0);
  useEffect(() => {
    if (!wanted || module.peek() !== null || module.failure() !== null) return;
    let live = true;
    const settle = () => {
      // Either outcome ends the wait: the module is then readable, or the failure is.
      if (live) arrived((n) => n + 1);
    };
    void module.load().then(settle, settle);
    return () => {
      live = false;
    };
  }, [module, wanted]);
  if (!wanted) return null;
  const failure = module.failure();
  if (failure !== null) throw failure;
  return module.peek();
}
